import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import Input from '../../../../../../../../common/data-entry/Input';
import useOutsideClickDetector from '../../../../../../../../common/hooks/useOutsideClickDetector';
import { NSPP_PATIENT } from '../../../../../../../../services/i18n/namespaces';
import { PAYMENT_TYPE_DIGITAL, PAYMENT_TYPE_PHYSICAL } from '../../../../constants';

import { isValidSsn } from './financeDetailsService';

import './FinanceDetails.scss';

const radiobuttonFontsConfig = {
  mb: '11px',
  '& .MuiFormControlLabel-label': {
    fontSize: '15px'
  }
};

export default function FinanceDetails({
  handleInputChange,
  isRequiredField,
  getValidationMessage,
  info,
  userCanManageFinanceDetails,
  handlePaymentTypeChange,
  originalPaymentType
}) {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [showValidationMessage, setShowValidationMessage] = useState(false);

  const { ssn, cardId, address, paymentType, cardAssignmentStatus } = info;
  const isUS = address.country === 'US';

  const dialogRef = useRef();

  useOutsideClickDetector(dialogRef, () => {
    setShowDialog(false);
  });

  useEffect(() => {
    if (ssn && isUS && !isValidSsn(ssn)) {
      setShowDialog(true);
      setShowValidationMessage(true);
    } else {
      setShowDialog(false);
      setShowValidationMessage(false);
    }
  }, [ssn, isUS]);

  const paymentTypeCheckboxesDisabled = !isUS || (!!originalPaymentType && !cardAssignmentStatus);

  const cardIdDisabled =
    !isUS || !userCanManageFinanceDetails || (info?.digitalPaymentAllowed && paymentType !== PAYMENT_TYPE_PHYSICAL);

  return (
    <div className="row">
      <div className="col-12 apm-form-label">
        <div className="edit-patient-modal-finance-details-header">
          {t('addOrEdit.financeDetails', { ns: NSPP_PATIENT })}
        </div>
        {info?.digitalPaymentAllowed && (
          <Tooltip
            title={paymentTypeCheckboxesDisabled ? t('addOrEdit.paymentTypeTooltip', { ns: NSPP_PATIENT }) : ''}
            placement="top"
          >
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="nameas"
                    checked={paymentType === PAYMENT_TYPE_PHYSICAL}
                    onChange={({ target: { checked } }) => onPaymentTypeChange(checked, PAYMENT_TYPE_PHYSICAL)}
                  />
                }
                label={t('addOrEdit.physical', { ns: NSPP_PATIENT })}
                sx={radiobuttonFontsConfig}
                disabled={paymentTypeCheckboxesDisabled}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="asd"
                    checked={paymentType === PAYMENT_TYPE_DIGITAL}
                    onChange={({ target: { checked } }) => onPaymentTypeChange(checked, PAYMENT_TYPE_DIGITAL)}
                  />
                }
                label={t('addOrEdit.digital', { ns: NSPP_PATIENT })}
                sx={radiobuttonFontsConfig}
                disabled={paymentTypeCheckboxesDisabled}
              />
            </FormGroup>
          </Tooltip>
        )}
        <Typography variant="body2" sx={{ color: '#F34848', pb: '11px' }}>
          {getValidationMessage('paymentType')}
        </Typography>
      </div>
      <div className="col-4">
        <Input
          label={t(isUS ? 'ssn' : 'sin', { ns: NSPP_PATIENT })}
          name="ssn"
          value={ssn}
          onChange={handleInputChange}
          required={isRequiredField('ssn')}
          validationMessage={
            showValidationMessage ? t('validation.invalidSsn', { ns: NSPP_PATIENT }) : getValidationMessage('ssn')
          }
          disabled={!userCanManageFinanceDetails}
        />
        {showDialog && (
          <div ref={dialogRef} className={'dropdown-menu-wrapper'}>
            <div className={'message'}>{t('validation.ssnFormat', { ns: NSPP_PATIENT })}</div>
          </div>
        )}
      </div>
      <div className="col-4">
        <Input
          disabled={cardIdDisabled}
          label={t('info.pid', { ns: NSPP_PATIENT })}
          name="cardId"
          value={cardId}
          onChange={handleInputChange}
          validationMessage={getValidationMessage('cardId')}
          required={isRequiredField('cardId')}
        />
      </div>
    </div>
  );

  function onPaymentTypeChange(checked, type) {
    const value = checked ? type : null;
    handlePaymentTypeChange(value);
  }
}
